import { Platform } from "@-/com.npmjs.react-native";

export const CARD_PREVIEW_TILE_MORPHEME_BOX_PADDING = 2;

/** Multi-character morpheme column width as a fraction of card size. */
export const CARD_PREVIEW_TILE_MULTI_CHARACTER_MORPHEME_BOX_WIDTH_RATIO = 1;

/** Glyphs and pinyin share the top half; gloss and stats own the bottom half. */
export const CARD_PREVIEW_TILE_MULTI_CHARACTER_MORPHEME_BOX_HEIGHT_RATIO = 0.5;

// Vertical budget for the pinyin row as a fraction of card size. Slightly
// above 10% so the fontSize (which has to shrink for diacritic headroom)
// still renders visibly large on small cards.
export const CARD_PREVIEW_TILE_PINYIN_LINE_HEIGHT_RATIO = 0.145;

// fontSize / lineHeight. Leaves headroom inside the line-box for the
// tone-mark diacritics (ǎ, ǐ, ǔ, …) and y/g descenders.
//
// Native RN keeps the line-box tight (0.9) because it doesn't clip glyphs that
// bleed past the line-box. RN-web renders the pinyin <Text> with
// `overflow: hidden` at exactly `lineHeight`, so any ink past the line-box
// bottom (the "g"/"y" descender) is clipped. A smaller ratio gives the same
// lineHeight more descender + diacritic room so nothing is cut off on web.
export const CARD_PREVIEW_TILE_PINYIN_FONT_RATIO = Platform.OS === "web" ? 0.72 : 0.9;

// Ratio of fontSize to lineHeight for the glyph. CJK glyphs need ascender
// slack — setting lineHeight = fontSize would clip the top of the character.
const GLYPH_FONT_RATIO = 0.83;

export function getMorphemeDetailsTileGlyphStyle({
  availableHeight,
}: {
  availableHeight: number;
}): {
  fontSize: number;
  lineHeight: number;
} {
  const paddedHeight = Math.max(0, availableHeight - CARD_PREVIEW_TILE_MORPHEME_BOX_PADDING * 2);
  const lineHeight = Math.max(1, paddedHeight);
  const fontSize = Math.max(1, Math.floor(lineHeight * GLYPH_FONT_RATIO));
  return { fontSize, lineHeight };
}

export function getMorphemeDetailsTileMultiCharacterMorphemeBoxWidth({
  tileSize,
}: {
  tileSize: number;
}): number {
  return Math.floor(tileSize * CARD_PREVIEW_TILE_MULTI_CHARACTER_MORPHEME_BOX_WIDTH_RATIO);
}

export function getMorphemeDetailsTileMultiCharacterMorphemeBoxHeight({
  tileSize,
}: {
  tileSize: number;
}): number {
  return Math.floor(tileSize * CARD_PREVIEW_TILE_MULTI_CHARACTER_MORPHEME_BOX_HEIGHT_RATIO);
}

/** Size multi-character glyphs to fill the morpheme column width without exceeding height. */
export function getMorphemeDetailsTileMultiCharacterGlyphStyle({
  availableHeight,
  availableWidth,
  characterCount,
}: {
  availableHeight: number;
  availableWidth: number;
  characterCount: number;
}): { fontSize: number; lineHeight: number } {
  const heightStyle = getMorphemeDetailsTileGlyphStyle({ availableHeight });
  if (characterCount <= 0) {
    return heightStyle;
  }

  const paddedWidth = Math.max(0, availableWidth - CARD_PREVIEW_TILE_MORPHEME_BOX_PADDING * 2);
  // CJK glyphs advance by one em: width constrains fontSize, not lineHeight.
  // Applying the vertical leading ratio here left wide words using only 83% of the row.
  const widthFontSize = Math.max(1, Math.floor(paddedWidth / characterCount));

  if (widthFontSize >= heightStyle.fontSize) {
    return heightStyle;
  }
  return {
    fontSize: widthFontSize,
    lineHeight: Math.min(heightStyle.lineHeight, Math.ceil(widthFontSize / GLYPH_FONT_RATIO)),
  };
}

// Fraction of the glyph line-box that sits empty below the Han ink. CJK glyphs
// have no descender, so the baseline-to-line-box-bottom region sits below the
// visible ink while only the half-leading sits above; we collapse this
// asymmetry by negatively offsetting the next row by it, so the gap below the
// glyph equals the gap above it.
//
// The fraction is platform-specific because RN and the browser allocate the
// line-box differently for the same CJK font. On iOS RN the ink ends ~30% of
// the lineHeight above the box bottom. The browser packs the ink much closer
// to the box bottom, so reusing 0.3 over-pulls the pinyin row up into the
// glyph ink (overlap). 0.08 matches the browser's far smaller below-ink slack.
const GLYPH_LINE_BOX_BELOW_INK_RATIO = Platform.OS === "web" ? 0.08 : 0.3;

// Difference between the empty space below the Han ink and the empty space
// above it inside the glyph row, plus the row's outer padding. We collapse this
// asymmetry by negatively offsetting the next row by this amount, so the gap
// below the glyph equals the gap above it.
export function getMorphemeDetailsTileGlyphBelowInkSlack({
  availableHeight,
}: {
  availableHeight: number;
}): number {
  const { lineHeight } = getMorphemeDetailsTileGlyphStyle({ availableHeight });
  const rowSlack = availableHeight - lineHeight;
  const lineBoxAsymmetry = lineHeight * GLYPH_LINE_BOX_BELOW_INK_RATIO;
  return Math.floor(rowSlack + lineBoxAsymmetry);
}

// Extra upward pull so the pinyin row sits tight under the glyph ink (beyond
// getMorphemeDetailsTileGlyphBelowInkSlack, which only balances line-box slack).
export const CARD_PREVIEW_TILE_PINYIN_TIGHTEN_BELOW_GLYPH = 2;

export function getMorphemeDetailsTilePinyinBelowGlyphMargin({
  glyphAvailableHeight,
}: {
  glyphAvailableHeight: number;
}): number {
  return (
    getMorphemeDetailsTileGlyphBelowInkSlack({ availableHeight: glyphAvailableHeight }) +
    CARD_PREVIEW_TILE_PINYIN_TIGHTEN_BELOW_GLYPH
  );
}

export function getMorphemeDetailsTilePinyinSizing({ tileSize }: { tileSize: number }): {
  fontSize: number;
  lineHeight: number;
} {
  const lineHeight = Math.max(
    1,
    Math.floor(tileSize * CARD_PREVIEW_TILE_PINYIN_LINE_HEIGHT_RATIO),
  );
  const fontSize = Math.max(1, Math.floor(lineHeight * CARD_PREVIEW_TILE_PINYIN_FONT_RATIO));
  return { fontSize, lineHeight };
}
